// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import { Button, CircularProgress, Stack } from '@mui/material'
import DialogBox, { DialogActionsAlignment } from '../DialogBox'

export type CancelConfirmationButton = 'Cancel' | 'Discard' | 'No'
export type ContinueConfirmationButton = 'Continue' | 'Proceed' | 'Yes'

/**
 * Props for the {@link ContinueDialogBox} component
 */
export interface ContinueDialogBoxProps {
  /** Controls whether the dialog is visible */
  open: boolean
  /** Toggles a {@link CircularProgress} component after clicking the continue button */
  loading?: boolean
  /** Alightment of the dialog actions (e.g., buttons, progressbar, etc) (see {@link DialogActionsAlignment}) */
  actionAlignment?: DialogActionsAlignment
  /** The title displayed at the top of the dialog */
  title: string
  /** The description or message shown in the dialog */
  description: string
  /** Text for the cancel button (see {@link CancelConfirmationButton}) */
  cancelButtonText?: CancelConfirmationButton
  /** Text for the continue button (see {@link ContinueConfirmationButton}) */
  continueButtonText?: ContinueConfirmationButton
  /** Custom content to render inside the dialog, below the description */
  content?: JSX.Element
  /** Callback fired when the cancel button is clicked */
  onCancel: () => void
  /** Callback fired when the continue button is clicked */
  onContinue: () => void
}

/**
 *  Dialog box component used to prompt users to continue or cancel an action
 *
 * @example
 * ```tsx
 * <ContinueDialogBox
 *   open={true}
 *   loading={false}
 *   actionAlignment="right"
 *   title="Confirm Action"
 *   description="Are you sure you want to continue?"
 *   cancelButtonText="Cancel"
 *   continueButtonText="Continue"
 *   content={<div>Extra details here</div>}
 *   onCancel={() => console.log('Cancelled')}
 *   onContinue={() => console.log('Continued')}
 * />
 * ```
 */
export default function ContinueDialogBox(
  {
    open,
    loading,
    actionAlignment,
    title,
    description,
    cancelButtonText,
    continueButtonText,
    content,
    onCancel,
    onContinue
  }: Readonly<ContinueDialogBoxProps>
): JSX.Element {
  return (
    <DialogBox
      open={open}
      title={title}
      description={description}
      content={content}
      actionAlignment={loading ? 'center' : actionAlignment}
      actions={(
        <Stack
          direction="row"
        >
          {
            loading
              ? <CircularProgress />
              : (
                <>
                  <Button
                    variant="contained"
                    onClick={onContinue}
                  >
                    {continueButtonText}
                  </Button>
                  <Button
                    variant="text"
                    onClick={onCancel}
                  >
                    {cancelButtonText}
                  </Button>
                </>
              )
          }
        </Stack>
          )}
    />
  )
}

ContinueDialogBox.defaultProps = {
  loading: false,
  cancelButtonText: 'Cancel',
  continueButtonText: 'Continue',
  actionAlignment: 'right'
}
